<template>
  <div class="w-full p-4 my-6">
    <smm-sub-nav />

    <div class="bg-white rounded-lg border border-gray-100 shadow-sm p-4 sm:p-6">
      <div class="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 class="text-lg font-semibold text-gray-900">{{ $t('smm.stats.title') }}</h2>
          <p class="text-sm text-gray-500 mt-0.5">{{ $t('smm.stats.subtitle') }}</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
            :disabled="loading || importingAccount"
            @click="load"
          >
            {{ $t('common.refresh') }}
          </button>
          <button
            v-for="acc in mailAccounts"
            :key="acc.key"
            type="button"
            class="px-3 py-2 text-sm rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
            :disabled="loading || !!importingAccount || !acc.configured || mailImapBlocked"
            :title="importButtonTitle(acc)"
            @click="runImport(acc.key)"
          >
            {{ importingAccount === acc.key
              ? $t('smm.stats.importing')
              : $t('smm.stats.import_account', { shop: shortAccountLabel(acc) }) }}
          </button>
        </div>
      </div>

      <p v-if="mailStatsLine" class="text-sm text-gray-600 mb-3">{{ mailStatsLine }}</p>

      <div class="flex flex-wrap items-center gap-2 mb-4">
        <select v-model="shopId" class="border rounded-lg px-3 py-2 text-sm bg-white" @change="load">
          <option value="">{{ $t('smm.stats.all_shops') }}</option>
          <option v-for="shop in shops" :key="shop.id" :value="String(shop.id)">{{ shop.name }}</option>
        </select>
        <label class="inline-flex items-center gap-2 text-sm text-gray-600">
          <span class="whitespace-nowrap">{{ $t('smm.stats.group_by') }}</span>
          <select
            v-model="periodMode"
            class="border rounded-lg px-3 py-2 text-sm bg-white text-gray-900"
            @change="onPeriodModeChange"
          >
            <option value="all">{{ $t('smm.stats.group_all_time') }}</option>
            <option value="month">{{ $t('smm.stats.group_monthly') }}</option>
          </select>
        </label>
        <div
          v-if="periodMode === 'month'"
          class="inline-flex items-center rounded-lg border border-gray-200 overflow-hidden"
        >
          <button
            type="button"
            class="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            :disabled="loading || importingAccount"
            :aria-label="$t('smm.stats.month_prev')"
            @click="shiftMonth(-1)"
          >
            ‹
          </button>
          <div class="min-w-[10.5rem] px-3 py-2 text-sm font-medium text-gray-900 text-center border-x border-gray-200 tabular-nums">
            {{ monthLabel }}
          </div>
          <button
            type="button"
            class="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            :disabled="loading || importingAccount || isCurrentMonth"
            :aria-label="$t('smm.stats.month_next')"
            @click="shiftMonth(1)"
          >
            ›
          </button>
        </div>
      </div>

      <p v-if="mailHint" class="text-sm text-amber-700 mb-3">{{ mailHint }}</p>
      <p v-if="error" class="text-sm text-red-600 mb-3">{{ error }}</p>
      <p v-if="importMessage" class="text-sm text-emerald-700 mb-3">{{ importMessage }}</p>

      <div v-if="loading" class="py-10 text-center text-gray-500 text-sm">{{ $t('common.loading') }}</div>

      <template v-else>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          <div class="rounded-lg border border-gray-100 p-3">
            <div class="text-xs text-gray-500">{{ $t('smm.stats.orders') }}</div>
            <div class="text-xl font-semibold text-gray-900 mt-1">{{ summary.orders || 0 }}</div>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <div class="text-xs text-gray-500">{{ $t('smm.stats.revenue') }}</div>
            <div class="text-xl font-semibold text-gray-900 mt-1">
              {{ formatMoney(summary.revenue, summary.currency) }}
            </div>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <div class="text-xs text-gray-500">{{ $t('smm.stats.timezone') }}</div>
            <div class="text-sm font-medium text-gray-900 mt-1">{{ summary.timezone || '—' }}</div>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <div class="text-xs text-gray-500">{{ $t('smm.stats.period') }}</div>
            <div class="text-sm font-medium text-gray-900 mt-1">{{ periodLabel }}</div>
          </div>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div class="rounded-lg border border-gray-100 p-3">
            <div class="text-xs text-gray-500">{{ $t('smm.stats.discounted_orders') }}</div>
            <div class="text-xl font-semibold text-gray-900 mt-1">
              {{ summary.discounted_orders ?? 0 }}
              <span class="text-sm font-normal text-gray-500">
                ({{ formatPct(summary.discounted_orders_pct) }})
              </span>
            </div>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <div class="text-xs text-gray-500">{{ $t('smm.stats.avg_discount_pct') }}</div>
            <div class="text-xl font-semibold text-gray-900 mt-1">{{ formatPct(summary.avg_discount_pct) }}</div>
            <div class="text-[11px] text-gray-400 mt-0.5">{{ $t('smm.stats.avg_discount_pct_hint') }}</div>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <div class="text-xs text-gray-500">{{ $t('smm.stats.discount_rate') }}</div>
            <div class="text-xl font-semibold text-gray-900 mt-1">{{ formatPct(summary.discount_rate) }}</div>
            <div class="text-[11px] text-gray-400 mt-0.5">{{ $t('smm.stats.discount_rate_hint') }}</div>
          </div>
          <div class="rounded-lg border border-gray-100 p-3">
            <div class="text-xs text-gray-500">{{ $t('smm.stats.discount_sum') }}</div>
            <div class="text-xl font-semibold text-gray-900 mt-1">
              {{ formatMoney(summary.discount_sum, summary.currency) }}
            </div>
            <div class="text-[11px] text-gray-400 mt-0.5">
              {{ $t('smm.stats.original_sum') }}: {{ formatMoney(summary.original_sum, summary.currency) }}
            </div>
          </div>
        </div>

        <p v-if="summary.orders_with_pricing != null && summary.orders_with_pricing < (summary.orders || 0)" class="text-xs text-amber-700 mb-4">
          {{ $t('smm.stats.pricing_partial', { n: summary.orders_with_pricing, total: summary.orders }) }}
        </p>

        <section class="mb-6">
          <h3 class="text-sm font-semibold text-gray-900 mb-1">{{ byDayTitle }}</h3>
          <p class="text-xs text-gray-500 mb-3">{{ $t('smm.stats.by_day_hint') }}</p>

          <div class="grid grid-cols-3 gap-3 mb-4">
            <div
              v-for="phase in byMonthPhase"
              :key="phase.key"
              class="rounded-lg border border-gray-100 p-3"
            >
              <div class="text-xs text-gray-500">
                {{ $t('smm.stats.month_phase.' + phase.key) }}
                <span class="text-gray-400">({{ phase.label_days }})</span>
              </div>
              <div class="text-xl font-semibold text-gray-900 mt-1">{{ phase.orders || 0 }}</div>
              <div class="text-[11px] text-gray-400 mt-0.5">
                {{ formatMoney(phase.revenue, summary.currency) }}
                <span v-if="phaseOrdersShare(phase)" class="ml-1">· {{ phaseOrdersShare(phase) }}</span>
              </div>
            </div>
          </div>

          <div class="rounded-lg border border-gray-100 p-4 mb-4">
            <div class="text-xs text-gray-500 mb-3">{{ $t('smm.stats.avg_orders_per_day') }}</div>
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <div class="text-[11px] text-gray-400">{{ $t('smm.stats.avg_orders_overall') }}</div>
                <div class="text-xl font-semibold text-gray-900 mt-0.5 tabular-nums">
                  {{ formatAvg(avgOrdersPerDay.overall) }}
                </div>
                <div class="text-[11px] text-gray-400 mt-0.5">
                  {{ $t('smm.stats.avg_orders_days', { n: avgOrdersPerDay.days }) }}
                </div>
              </div>
              <div
                v-for="phase in avgOrdersPerDay.phases"
                :key="'avg-' + phase.key"
              >
                <div class="text-[11px] text-gray-400">
                  {{ $t('smm.stats.month_phase.' + phase.key) }}
                </div>
                <div class="text-xl font-semibold text-gray-900 mt-0.5 tabular-nums">
                  {{ formatAvg(phase.avg) }}
                </div>
                <div class="text-[11px] text-gray-400 mt-0.5">
                  {{ $t('smm.stats.avg_orders_days', { n: phase.days }) }}
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="byDay.length"
            class="flex items-end gap-px sm:gap-0.5 h-44 border-b border-gray-200 pb-0 overflow-x-auto"
          >
            <div
              v-for="row in byDay"
              :key="'day-' + (row.date || row.day)"
              class="flex-1 min-w-[8px] sm:min-w-0 h-full flex flex-col items-center justify-end group relative"
              :title="dayBarTitle(row)"
            >
              <span
                class="text-[9px] text-gray-500 tabular-nums mb-0.5 opacity-0 group-hover:opacity-100 sm:opacity-100"
              >{{ row.orders || '' }}</span>
              <div
                class="w-full max-w-[18px] mx-auto rounded-t transition-colors"
                :class="dayBarClass(row.day)"
                :style="{ height: dayBarHeight(row.orders) }"
              />
              <span
                class="text-[9px] text-gray-400 mt-1 tabular-nums leading-none"
                :class="row.day % 5 === 0 || row.day === 1 ? 'opacity-100' : 'opacity-0 sm:opacity-40'"
              >{{ row.day }}</span>
            </div>
          </div>
          <div v-else class="py-6 text-center text-gray-500 text-sm">{{ $t('smm.stats.empty') }}</div>
        </section>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <section>
            <h3 class="text-sm font-semibold text-gray-900 mb-2">{{ $t('smm.stats.by_hour') }}</h3>
            <div class="space-y-0.5">
              <div
                v-for="row in byHour"
                :key="'h-' + row.hour"
                class="flex items-center gap-2 text-xs"
              >
                <span class="w-10 text-gray-500 tabular-nums">{{ pad(row.hour) }}:00</span>
                <div class="flex-1 h-2.5 bg-gray-100 rounded overflow-hidden">
                  <div
                    class="h-full bg-blue-500/80"
                    :style="{ width: barWidth(row.orders, maxHourOrders) }"
                  />
                </div>
                <span class="w-24 text-right text-gray-700 tabular-nums">{{ row.orders }} / {{ formatMoney(row.revenue, summary.currency) }}</span>
              </div>
            </div>
          </section>

          <section>
            <h3 class="text-sm font-semibold text-gray-900 mb-2">{{ $t('smm.stats.by_time_of_day') }}</h3>
            <div class="space-y-2">
              <div
                v-for="row in byTimeOfDay"
                :key="row.key"
                class="flex items-center gap-2 text-sm"
              >
                <span class="w-24 text-gray-600">{{ $t('smm.stats.tod.' + row.key) }}</span>
                <div class="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                  <div
                    class="h-full bg-emerald-500/80"
                    :style="{ width: barWidth(row.orders, maxTodOrders) }"
                  />
                </div>
                <span class="w-28 text-right text-gray-700 text-xs tabular-nums">{{ row.orders }} / {{ formatMoney(row.revenue, summary.currency) }}</span>
              </div>
            </div>

            <h3 class="text-sm font-semibold text-gray-900 mb-2 mt-6">{{ $t('smm.stats.by_weekday') }}</h3>
            <div class="overflow-x-auto">
              <table class="min-w-full text-xs">
                <thead>
                  <tr class="text-left text-gray-500 border-b">
                    <th class="py-1.5 pr-2 font-medium">{{ $t('smm.stats.weekday_col') }}</th>
                    <th class="py-1.5 pr-2 font-medium text-right">{{ $t('smm.stats.orders') }}</th>
                    <th class="py-1.5 pr-2 font-medium text-right">{{ $t('smm.stats.full_price_orders') }}</th>
                    <th class="py-1.5 pr-2 font-medium text-right">{{ $t('smm.stats.discounted_orders_short') }}</th>
                    <th class="py-1.5 pr-2 font-medium text-right">%</th>
                    <th class="py-1.5 pr-2 font-medium text-right">{{ $t('smm.stats.avg_discount_pct_short') }}</th>
                    <th class="py-1.5 font-medium text-right">{{ $t('smm.stats.discount_rate_short') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in byWeekday" :key="'d-' + row.weekday" class="border-b border-gray-50">
                    <td class="py-1.5 pr-2 text-gray-700">{{ weekdayLabel(row.weekday) }}</td>
                    <td class="py-1.5 pr-2 text-right tabular-nums">{{ row.orders }}</td>
                    <td class="py-1.5 pr-2 text-right tabular-nums">{{ row.full_price_orders ?? '—' }}</td>
                    <td class="py-1.5 pr-2 text-right tabular-nums">{{ row.discounted_orders ?? '—' }}</td>
                    <td class="py-1.5 pr-2 text-right tabular-nums">{{ formatPct(row.discounted_orders_pct) }}</td>
                    <td class="py-1.5 pr-2 text-right tabular-nums">{{ formatPct(row.avg_discount_pct) }}</td>
                    <td class="py-1.5 text-right tabular-nums">{{ formatPct(row.discount_rate) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <section>
          <h3 class="text-sm font-semibold text-gray-900 mb-2">{{ $t('smm.stats.top_products') }}</h3>
          <div v-if="!topProducts.length" class="py-6 text-center text-gray-500 text-sm">
            {{ $t('smm.stats.empty') }}
          </div>
          <div v-else class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-gray-500 border-b">
                  <th class="py-2 pr-3 font-medium">{{ $t('smm.stats.product') }}</th>
                  <th class="py-2 pr-3 font-medium text-right">
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 hover:text-gray-800"
                      @click="toggleProductSort('units')"
                    >
                      {{ $t('smm.stats.units') }}
                      <span class="tabular-nums text-[10px]">{{ productSortMark('units') }}</span>
                    </button>
                  </th>
                  <th class="py-2 font-medium text-right">
                    <button
                      type="button"
                      class="inline-flex items-center gap-1 hover:text-gray-800"
                      @click="toggleProductSort('revenue')"
                    >
                      {{ $t('smm.stats.revenue') }}
                      <span class="tabular-nums text-[10px]">{{ productSortMark('revenue') }}</span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, idx) in sortedTopProducts" :key="idx" class="border-b border-gray-50">
                  <td class="py-2 pr-3 text-gray-900">
                    <div>{{ p.product_name }}</div>
                    <div v-if="p.etsy_listing_id" class="text-[11px] text-gray-400 mt-0.5 font-mono">
                      #{{ p.etsy_listing_id }}
                    </div>
                    <div
                      v-else-if="p.name_variants && p.name_variants.length > 1"
                      class="text-[11px] text-amber-700 mt-0.5"
                      :title="p.name_variants.join('\n')"
                    >
                      {{ $t('smm.stats.name_variants', { n: p.name_variants.length }) }}
                    </div>
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums text-gray-700">{{ p.units }}</td>
                  <td class="py-2 text-right tabular-nums text-gray-700">{{ formatMoney(p.revenue, summary.currency) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="mt-8">
          <h3 class="text-sm font-semibold text-gray-900 mb-2">{{ $t('smm.stats.by_promo') }}</h3>
          <div v-if="!byPromoCode.length" class="py-6 text-center text-gray-500 text-sm">
            {{ $t('smm.stats.empty') }}
          </div>
          <div v-else class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead>
                <tr class="text-left text-xs text-gray-500 border-b">
                  <th class="py-2 pr-3 font-medium">{{ $t('smm.stats.promo_code') }}</th>
                  <th class="py-2 pr-3 font-medium text-right">{{ $t('smm.stats.units') }}</th>
                  <th class="py-2 font-medium text-right">{{ $t('smm.stats.promo_discount_pct') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in byPromoCode" :key="'promo-' + idx" class="border-b border-gray-50">
                  <td class="py-2 pr-3 text-gray-900">
                    {{ row.promo_code ? row.promo_code : $t('smm.stats.promo_none') }}
                  </td>
                  <td class="py-2 pr-3 text-right tabular-nums text-gray-700">{{ row.units }}</td>
                  <td class="py-2 text-right tabular-nums text-gray-700">{{ formatPct(row.avg_discount_pct) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import SmmSubNav from '@/components/smm/SmmSubNav.vue'

function currentMonthCursor() {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() } // month 0–11
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

function pad2(n) {
  return String(n).padStart(2, '0')
}

export default {
  components: { SmmSubNav },
  data() {
    const cursor = currentMonthCursor()
    return {
      loading: false,
      importingAccount: null,
      error: '',
      importMessage: '',
      shopId: '',
      viewYear: cursor.year,
      viewMonth: cursor.month,
      periodMode: 'month', // month | all
      shops: [],
      mail: { configured: false, imap_extension: false, host: null, orders_in_db: 0, accounts: [], imap_blocked: false },
      lastImport: null,
      summary: {},
      byHour: [],
      byDay: [],
      byDayMode: 'date',
      byMonthPhase: [],
      byTimeOfDay: [],
      byWeekday: [],
      topProducts: [],
      byPromoCode: [],
      productSortKey: 'revenue',
      productSortDir: 'desc',
    }
  },
  computed: {
    sortedTopProducts() {
      const key = this.productSortKey
      const dir = this.productSortDir === 'asc' ? 1 : -1
      return [...this.topProducts].sort((a, b) => {
        const av = Number(a?.[key] ?? 0)
        const bv = Number(b?.[key] ?? 0)
        if (av === bv) {
          return String(a.product_name || '').localeCompare(String(b.product_name || ''))
        }
        return av < bv ? -dir : dir
      })
    },
    mailAccounts() {
      const list = this.mail?.accounts
      if (Array.isArray(list) && list.length) return list
      return [
        { key: 'diy', label: 'DIY and Mint Tea', configured: false },
        { key: 'bss', label: 'Beginner Sewing Studio', configured: false },
      ]
    },
    from() {
      return `${this.viewYear}-${pad2(this.viewMonth + 1)}-01`
    },
    to() {
      return `${this.viewYear}-${pad2(this.viewMonth + 1)}-${pad2(daysInMonth(this.viewYear, this.viewMonth))}`
    },
    isCurrentMonth() {
      const cur = currentMonthCursor()
      return this.viewYear === cur.year && this.viewMonth === cur.month
    },
    monthLabel() {
      const locale = this.$i18n?.locale === 'ru' ? 'ru-RU' : 'en-US'
      const d = new Date(this.viewYear, this.viewMonth, 1)
      try {
        const label = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(d)
        return label.charAt(0).toUpperCase() + label.slice(1)
      } catch {
        return `${pad2(this.viewMonth + 1)}.${this.viewYear}`
      }
    },
    periodLabel() {
      if (this.periodMode === 'all') {
        return this.$t('smm.stats.group_all_time')
      }
      return this.monthLabel
    },
    maxHourOrders() {
      return Math.max(1, ...this.byHour.map((r) => Number(r.orders) || 0))
    },
    maxDayOrders() {
      return Math.max(1, ...this.byDay.map((r) => Number(r.orders) || 0))
    },
    byDayTitle() {
      return this.byDayMode === 'day_of_month'
        ? this.$t('smm.stats.by_day_of_month')
        : this.$t('smm.stats.by_day')
    },
    phaseOrdersTotal() {
      return this.byMonthPhase.reduce((sum, p) => sum + (Number(p.orders) || 0), 0)
    },
    avgOrdersPerDay() {
      const days = this.byDay || []
      const totalOrders = days.reduce((sum, r) => sum + (Number(r.orders) || 0), 0)
      const dayCount = days.length
      const phaseRanges = [
        { key: 'start', min: 1, max: 10 },
        { key: 'middle', min: 11, max: 20 },
        { key: 'end', min: 21, max: 31 },
      ]
      const phases = phaseRanges.map(({ key, min, max }) => {
        const slice = days.filter((r) => {
          const d = Number(r.day) || 0
          return d >= min && d <= max
        })
        const orders = slice.reduce((sum, r) => sum + (Number(r.orders) || 0), 0)
        const n = slice.length
        return {
          key,
          days: n,
          avg: n > 0 ? orders / n : 0,
        }
      })
      return {
        days: dayCount,
        overall: dayCount > 0 ? totalOrders / dayCount : 0,
        phases,
      }
    },
    maxTodOrders() {
      return Math.max(1, ...this.byTimeOfDay.map((r) => Number(r.orders) || 0))
    },
    mailImapBlocked() {
      return !!this.mail?.imap_blocked
    },
    mailHint() {
      if (!this.mail?.imap_extension) {
        return this.$t('smm.stats.mail_imap_missing')
      }
      if (this.mailImapBlocked) {
        return this.$t('smm.stats.mail_imap_blocked')
      }
      const any = this.mailAccounts.some((a) => a.configured)
      if (!any) {
        return this.$t('smm.stats.mail_not_configured')
      }
      return ''
    },
    mailStatsLine() {
      const db = this.mail?.orders_in_db ?? this.summary?.orders ?? 0
      const box = this.lastImport?.mailbox_total
      const label = this.lastImport?.account_label || this.lastImport?.account
      if (box != null && label) {
        return this.$t('smm.stats.mail_counts_account', { db, mailbox: box, shop: label })
      }
      if (box != null) {
        return this.$t('smm.stats.mail_counts', { db, mailbox: box })
      }
      if (db) {
        return this.$t('smm.stats.mail_counts_db_only', { db })
      }
      return ''
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    shortAccountLabel(acc) {
      if (!acc) return ''
      if (acc.key === 'diy') return 'DIY'
      if (acc.key === 'bss') return 'BSS'
      return acc.label || acc.key
    },
    shiftMonth(delta) {
      if (this.periodMode !== 'month') return
      if (delta > 0 && this.isCurrentMonth) return
      let y = this.viewYear
      let m = this.viewMonth + delta
      while (m < 0) {
        m += 12
        y -= 1
      }
      while (m > 11) {
        m -= 12
        y += 1
      }
      const cur = currentMonthCursor()
      if (y > cur.year || (y === cur.year && m > cur.month)) {
        y = cur.year
        m = cur.month
      }
      this.viewYear = y
      this.viewMonth = m
      this.load()
    },
    onPeriodModeChange() {
      this.load()
    },
    pad(n) {
      return String(n).padStart(2, '0')
    },
    barWidth(value, max) {
      const pct = Math.round((Number(value) / Math.max(1, max)) * 100)
      return `${Math.min(100, Math.max(0, pct))}%`
    },
    dayBarHeight(value) {
      const pct = Math.round((Number(value) / this.maxDayOrders) * 100)
      const h = Math.min(100, Math.max(0, pct))
      return h === 0 && Number(value) === 0 ? '2px' : `${Math.max(h, Number(value) > 0 ? 4 : 0)}%`
    },
    dayBarClass(day) {
      if (day <= 10) return 'bg-emerald-500/80 group-hover:bg-emerald-600'
      if (day <= 20) return 'bg-blue-500/80 group-hover:bg-blue-600'
      return 'bg-violet-500/80 group-hover:bg-violet-600'
    },
    dayBarTitle(row) {
      const money = this.formatMoney(row.revenue, this.summary.currency)
      if (row.date) {
        return `${row.date}: ${row.orders} · ${money}`
      }
      return `${this.$t('smm.stats.day_n', { n: row.day })}: ${row.orders} · ${money}`
    },
    phaseOrdersShare(phase) {
      const total = this.phaseOrdersTotal
      if (!total) return ''
      const pct = Math.round((Number(phase.orders) / total) * 1000) / 10
      return `${pct}%`
    },
    formatMoney(amount, currency) {
      const n = Number(amount || 0)
      const cur = currency || 'USD'
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n)
      } catch {
        return `${n.toFixed(2)} ${cur}`
      }
    },
    formatPct(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      if (Number.isNaN(n)) return '—'
      return `${n.toFixed(1)}%`
    },
    formatAvg(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(n >= 10 ? 1 : 2)
    },
    weekdayLabel(day) {
      const keys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
      return this.$t('smm.stats.weekday.' + (keys[day] || 'sun'))
    },
    toggleProductSort(key) {
      if (this.productSortKey === key) {
        this.productSortDir = this.productSortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.productSortKey = key
        this.productSortDir = 'desc'
      }
    },
    productSortMark(key) {
      if (this.productSortKey !== key) return ''
      return this.productSortDir === 'asc' ? '↑' : '↓'
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const params = {}
        if (this.shopId) params.shop_id = this.shopId
        if (this.periodMode === 'all') {
          params.all_time = 1
        } else {
          params.from = this.from
          params.to = this.to
        }
        const { data } = await axios.get('/api/v1/smm/etsy-stats', { params })
        const payload = data.data || {}
        this.summary = payload.summary || {}
        this.byHour = payload.by_hour || []
        this.byDay = payload.by_day || []
        this.byDayMode = payload.by_day_mode || 'date'
        this.byMonthPhase = payload.by_month_phase || []
        this.byTimeOfDay = payload.by_time_of_day || []
        this.byWeekday = payload.by_weekday || []
        this.topProducts = payload.top_products || []
        this.byPromoCode = payload.by_promo_code || []
        this.shops = payload.shops || []
        this.mail = payload.mail || { configured: false, imap_extension: false, accounts: [] }
        if (payload.mail?.orders_in_db != null) {
          this.mail.orders_in_db = payload.mail.orders_in_db
        }
      } catch (e) {
        this.error = e?.response?.data?.message || e.message || 'Failed to load stats'
      } finally {
        this.loading = false
      }
    },
    importButtonTitle(acc) {
      if (this.mailImapBlocked) return this.$t('smm.stats.mail_imap_blocked')
      if (!acc?.configured) return this.$t('smm.stats.mail_account_not_configured', { account: acc.label })
      return acc.username || ''
    },
    async runImport(accountKey) {
      this.importingAccount = accountKey
      this.importMessage = ''
      this.error = ''
      try {
        const { data } = await axios.post(
          '/api/v1/smm/etsy-stats/import',
          { account: accountKey, batch: 5 },
          { timeout: 90000 },
        )
        const s = data.data || {}
        this.lastImport = s
        this.mail = { ...this.mail, imap_blocked: false }
        this.applyImportStats(s, accountKey)
        await this.load()
      } catch (e) {
        this.importMessage = ''
        const body = e?.response?.data
        if (body?.code === 'imap_blocked' || body?.probe?.ok === false) {
          this.mail = { ...this.mail, imap_blocked: true }
        }
        this.error = body?.message || e.message || 'Import failed'
      } finally {
        this.importingAccount = null
      }
    },
    applyImportStats(s, accountKey) {
      const shop = s.account_label || accountKey
      if (s.fast) {
        this.importMessage = this.$t('smm.stats.import_fast_done_account', {
          shop,
          db: s.orders_in_db ?? '—',
          mailbox: s.mailbox_total ?? '—',
        })
        return
      }
      this.importMessage = this.$t('smm.stats.import_result_account', {
        shop,
        scanned: s.scanned ?? 0,
        imported: s.imported ?? 0,
        skipped: s.skipped ?? 0,
        failed: s.failed ?? 0,
      })
      if ((s.updated ?? 0) > 0) {
        this.importMessage += ' ' + this.$t('smm.stats.import_updated', { n: s.updated })
      }
      if (s.mailbox_total != null) {
        this.importMessage += ' ' + this.$t('smm.stats.import_mailbox_total', {
          n: s.mailbox_total,
          db: s.orders_in_db ?? '—',
        })
      }
      if (s.done) {
        this.importMessage += ' ' + this.$t('smm.stats.import_done')
      } else if ((s.imported ?? 0) === 0 && (s.updated ?? 0) === 0) {
        this.importMessage += ' ' + this.$t('smm.stats.import_zero_hint', {
          examined: s.examined ?? 0,
          already: s.already_in_db ?? 0,
        })
      }
      if (s.next_skip != null && !s.done) {
        this.importMessage += ' ' + this.$t('smm.stats.import_next_hint', {
          skip: s.next_skip,
          batch: s.batch ?? 5,
        })
      }
    },
  },
}
</script>
